import { z } from "zod";

import { type ViewColumnDef } from "@moment/api-collab/api-types";
import { type Store } from "@moment/api-collab/differential";
import { type DatabaseTables } from "@moment/api-collab/differential/types";
import { type ColorVariant } from "@moment/design-system/Chip";

import { jsonPathToMergePatchPath } from "../../../../api-collab/lib/differential/jsonTable/jsonPathToMergePatchPath";
import { pluck } from "../../../../api-collab/lib/differential/jsonTable/pluck";
import { type TopLevelQuerySpec } from "../../../../api-collab/lib/differential/operatorsV2/QuerySpec";
import { CLEAR_SELECT_ITEM_VALUE } from "../Select";
import { type Reaction, reactionSchema } from "./Reaction";
import { type User, userSchema } from "./User";
import { stringifyValue } from "./stringifyValue";

export type HydratedCell =
	| {
			type: "Text";
			header: string;
			value: string;
			error: string | undefined;
			update: (value: string) => void;
			disabled: boolean;
			visibleOnCard: boolean;
	  }
	| {
			type: "Reaction";
			header: string;
			value: Reaction[];
			error: string | undefined;
			toggleReaction: (user: User) => void;
			disabled: boolean;
			visibleOnCard: boolean;
	  }
	| {
			type: "Select";
			header: string;
			value: string;
			typeInfo?: { options: { label: string; color: ColorVariant }[] };
			error: string | undefined;
			update: (value: string | null | typeof CLEAR_SELECT_ITEM_VALUE) => void;
			disabled: boolean;
			visibleOnCard: boolean;
	  }
	| {
			type: "Person";
			header: string;
			value: User | undefined;
			error: string | undefined;
			update: (value: User | null) => void;
			disabled: boolean;
			visibleOnCard: boolean;
	  };

export const hydrateCell = (
	row: unknown,
	columnDef: ViewColumnDef,
	store: Store<DatabaseTables> | null,
	linkedRecords: Record<string, string>,
	query: TopLevelQuerySpec,
	_disabled: boolean
): HydratedCell => {
	const header = columnDef.header ?? columnDef.accessorKey.replace(/^\$\./, "");
	const unknownValue = pluck(row, columnDef.accessorKey);
	const table = linkedRecords[columnDef.accessorKey] ?? query.spec[0].table;
	const path = jsonPathToMergePatchPath(columnDef.accessorKey);
	const visibleOnCard = columnDef.visibleOnCard ?? true;

	// FIXME: We need a notion of a primary key, lol.
	const recordId: string | undefined = row?.["id"];
	const disabled = _disabled || !recordId || columnDef.readonly || false;

	switch (columnDef.type) {
		case "Text": {
			const { value, error } = stringifyValue(unknownValue);
			return {
				type: "Text",
				header,
				value,
				error,
				update: (value) => {
					if (!recordId) {
						throw new Error("Record ID is required");
					}
					void store?.applyMutation(table, {
						type: "UPDATE",
						recordId,
						change: {
							op: pluck(row, columnDef.accessorKey) === undefined ? "add" : "replace",
							path,
							value,
						},
					});
				},
				disabled,
				visibleOnCard,
			};
		}
		case "Reaction": {
			const reactions = z.array(reactionSchema).safeParse(unknownValue);
			const value = reactions.success ? reactions.data : [];
			const error = reactions.success ? undefined : "Invalid reactions";
			return {
				type: "Reaction",
				header,
				value,
				error,
				toggleReaction: (user) => {
					const reactionRecordId = crypto.randomUUID();
					const reaction = value.find((r) => r.user.id === user.id);
					if (reaction) {
						void store?.delete(table, reaction.id);
					} else {
						if (!recordId) {
							throw new Error("Record ID is required");
						}
						void store?.insert(table, reactionRecordId, {
							id: reactionRecordId,
							linkedRecordId: recordId,
							user,
							createdAt: Date.now(),
						} satisfies Reaction);
					}
				},
				disabled,
				visibleOnCard,
			};
		}
		case "Select": {
			const { value, error } = stringifyValue(unknownValue);
			return {
				type: "Select",
				header,
				value,
				typeInfo: columnDef.typeInfo,
				error,
				update: (_value: string | null | typeof CLEAR_SELECT_ITEM_VALUE) => {
					const value =
						_value === CLEAR_SELECT_ITEM_VALUE ? null : _value === "" ? null : _value;

					if (!recordId) {
						throw new Error("Record ID is required");
					}
					const op = pluck(row, columnDef.accessorKey) === undefined ? "add" : "replace";
					void store?.applyMutation(table, {
						type: "UPDATE",
						recordId,
						change: { op, path, value },
					});
				},
				disabled,
				visibleOnCard,
			};
		}
		case "Person": {
			const user = userSchema.safeParse(unknownValue);
			const value = user.success ? user.data : undefined;
			const error = user.success ? undefined : "Invalid user";
			return {
				type: "Person",
				header,
				value,
				error,
				update: (user: User | null) => {
					if (!recordId) {
						throw new Error("Record ID is required");
					}
					const op = pluck(row, columnDef.accessorKey) === undefined ? "add" : "replace";
					void store?.applyMutation(table, {
						type: "UPDATE",
						recordId,
						change: { op, path, value: user },
					});
				},
				disabled,
				visibleOnCard,
			};
		}
		default: {
			const exhaustive: never = columnDef;
			throw new Error(`Unknown column type: ${(exhaustive as ViewColumnDef)?.type}`);
		}
	}
};
